import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface TrainingStatus {
  enabled: boolean;
  vectorReady: boolean;
  totalIndexed: number;
  byChannel: Array<{ channel: string; count: number }>;
  recentlyIndexed: IndexedConversation[];
}

interface IndexedConversation {
  conversationId: number;
  indexedAt: string;
  chunksIndexed: number;
  csatScore: number | null;
  channel: string | null;
}

interface ConversationsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: IndexedConversation[];
}

export default function AIChatbotSettingsTrainingTabConversations() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [status, setStatus] = useState<TrainingStatus | null>(null);
  const [conversations, setConversations] = useState<IndexedConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<number | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [statusResp, convsResp] = await Promise.all([
        api.admin.get("ConversationTraining/Status"),
        api.admin.get("ConversationTraining/Conversations?pageSize=20")
      ]);
      const [s, c] = await Promise.all([statusResp.json(), convsResp.json()]);
      setStatus(s ?? null);
      const convResp = c as ConversationsResponse;
      setConversations(Array.isArray(convResp?.items) ? convResp.items : []);
    } catch {
      setStatus(null);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }

  async function removeConversation(conversationId: number) {
    setRemoving(conversationId);
    try {
      const resp = await api.admin.delete(`ConversationTraining/Conversation/${conversationId}`);
      const data = await resp.json();
      if (data.success) {
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        toastAlert.displayToast(Severity.Success, "Conversation removed from training index.");
      } else {
        toastAlert.displayToast(Severity.Error, data.message || "Failed to remove conversation.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove conversation.");
    } finally {
      setRemoving(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Status cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-800">{status?.totalIndexed ?? 0}</div>
          <div className="text-xs text-slate-500 mt-0.5">Total conversations indexed</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-800">{status?.byChannel?.length ?? 0}</div>
          <div className="text-xs text-slate-500 mt-0.5">Channels with indexed conversations</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className={`text-sm font-semibold ${status?.enabled ? "text-green-600" : "text-slate-400"}`}>
            {status?.enabled ? "Auto-training active" : "Auto-training off"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {status?.enabled
              ? "Resolved chats are automatically indexed as training data"
              : "Enable via Database Tool → EnableConversationTraining"}
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="text-sm font-semibold text-blue-800 mb-2">How conversation training works</div>
        <div className="space-y-1.5">
          {[
            "When a conversation resolves, Velaro scores it for training quality: 4+ messages, agent responded, CSAT ≥ 4",
            "High-quality conversations are automatically indexed to the bot's vector knowledge base",
            "The bot uses these past conversations to give better answers to similar future questions",
            "You can remove any conversation from the index below — it will not affect the original conversation record"
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-blue-700">
              <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                <path d="M3 8l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Conversation list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Indexed conversations</div>
          <div className="text-xs text-slate-400">Showing last 20</div>
        </div>

        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-slate-400">
            <svg viewBox="0 0 48 48" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="mb-2 opacity-30">
              <path d="M8 40V16a4 4 0 0 1 4-4h24a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H14l-6 4z" strokeLinejoin="round" />
              <path d="M16 22h16M16 28h10" strokeLinecap="round" />
            </svg>
            <p className="text-sm">No conversations indexed yet.</p>
            <p className="text-xs mt-1">
              {status?.enabled
                ? "Conversations will appear here automatically as they resolve and meet quality criteria."
                : "Enable auto-training in Database Tool → EnableConversationTraining."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {conversations.map((conv) => (
              <div key={conv.conversationId} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">
                      Conversation #{conv.conversationId}
                    </span>
                    {conv.csatScore != null && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                        CSAT {conv.csatScore}/5
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {conv.chunksIndexed} chunks indexed · Channel: {conv.channel || "web"} · Indexed {new Date(conv.indexedAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={removing === conv.conversationId}
                  onClick={() => removeConversation(conv.conversationId)}
                  className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-40"
                  aria-label={`Remove conversation ${conv.conversationId} from training index`}
                >
                  {removing === conv.conversationId ? "Removing..." : "Remove"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
